import {formatDate} from "@angular/common";
import {GetProcessingRequestsParams, ProcessingRequestHistoryItem} from "@app/_models/api";
import {EdiFileSummaryBaseComponent} from "@app/processing/edi-file-summary-base/edi-file-summary-base.component";

export const STUCK_AFTER_MS = 2 * 60 * 60 * 1000;
export const DEFAULT_WINDOW_DAYS = 30;

const LOCALE = "en-US";

export type RequestHistoryFilters = {
    requestType: string;
    fileType: string;
    status: string;
    customerId: string;
    fileName: string;
    requestedBy: string;
    fileGuid: string;
    from: string;
    to: string;
};

export function defaultFilters(today: Date = new Date()): RequestHistoryFilters {
    const from = new Date(today);
    from.setDate(from.getDate() - DEFAULT_WINDOW_DAYS);

    return {
        requestType: "",
        fileType: "",
        status: "",
        customerId: "",
        fileName: "",
        requestedBy: "",
        fileGuid: "",
        from: formatDate(from, "yyyy-MM-dd", LOCALE),
        to: formatDate(today, "yyyy-MM-dd", LOCALE)
    };
}

export function toRequestParams(filters: RequestHistoryFilters): GetProcessingRequestsParams {
    return {
        requestType: filters.requestType,
        fileType: filters.fileType,
        status: filters.status,
        customerId: filters.customerId,
        fileName: filters.fileName.trim(),
        requestedBy: filters.requestedBy.trim(),
        fileGuid: filters.fileGuid,
        // A file GUID request is not limited to a date window.
        from: filters.fileGuid ? "" : filters.from,
        to: filters.fileGuid ? "" : filters.to
    };
}

export function parseUtc(value: string): Date {
    return new Date(value.endsWith("Z") ? value : value + "Z");
}

export function isStuck(item: ProcessingRequestHistoryItem, now: Date = new Date()): boolean {
    return item.status === "IN_PROGRESS" && now.getTime() - parseUtc(item.updatedAt).getTime() > STUCK_AFTER_MS;
}

export function formatDateTime(value: string): string {
    return value ? formatDate(parseUtc(value), "yyyy-MM-dd, hh:mm a", LOCALE) : "";
}

export function dayKey(value: string): string {
    return formatDate(parseUtc(value), "yyyy-MM-dd", LOCALE);
}

export function dayHeading(value: string): string {
    return formatDate(parseUtc(value), "EEEE, MMMM d, y", LOCALE);
}

export function sortNewestFirst(items: ProcessingRequestHistoryItem[]): ProcessingRequestHistoryItem[] {
    return [...items].sort((a, b) => parseUtc(b.createdAt).getTime() - parseUtc(a.createdAt).getTime());
}

export function buildCsv(items: ProcessingRequestHistoryItem[]): string {
    const header = ["Request Type", "Created", "Requested By", "Status", "File Type", "File Name", "File GUID", "Customer", "Updated"];
    const rows = items.map(item => [
        item.requestType,
        item.createdAt ? formatDate(parseUtc(item.createdAt), "yyyy-MM-dd HH:mm", LOCALE) : "",
        item.requestedBy ?? "",
        item.status,
        item.fileType,
        item.fileName ?? "",
        item.fileGuid,
        item.customerName ?? "",
        item.updatedAt ? formatDate(parseUtc(item.updatedAt), "yyyy-MM-dd HH:mm", LOCALE) : ""
    ]);

    return [header, ...rows]
        .map(row => row.map(EdiFileSummaryBaseComponent.toCsvField).join(","))
        .join("\r\n");
}
